import { useMemo } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { Text } from '@antimatterhq/ui/components/typography';
import { Trend } from '@antimatterhq/ui/components/trend';
import { BarChart } from '@antimatterhq/ui/components/barChart';
import { count } from '@antimatterhq/ui/utils/format';
import { useGetDatasourcesSummaryWithSuspense } from '@/hooks/dbui/useGetDataSourcesSummary';
import { BaseHomepageSummaryCard } from '@/components/homepage/summaryCard/BaseHomepageSummaryCard';
import { HomepageSummaryCardMainMetric } from '@/components/homepage/summaryCard/HomepageSummaryCardMainMetric';
import HomepageSummaryCardSkeleton from '@/pages/home/_partials/HomepageSummaryCardSkeleton';
import { DefaultErrorBoundaryFallback } from '@/components/errorBoundary/ErrorBoundary';

const useGetDatasourcesSummaryWithSuspenseArgs = {
  alerts: true,
  rollupMetrics: true,
};

export const DataSourceHomepageSummaryCard = () => {
  const { data } = useGetDatasourcesSummaryWithSuspense(
    useGetDatasourcesSummaryWithSuspenseArgs,
  );

  const barChartData = useMemo(() => {
    return (data?.rollupMetrics?.eventsSparkLine ?? []).map((value) => ({
      value,
    }));
  }, [data?.rollupMetrics?.eventsSparkLine]);

  return (
    <BaseHomepageSummaryCard title="Datasources" to={'/configure/data-sources'}>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 grid grid-cols-2 gap-4">
          <HomepageSummaryCardMainMetric
            label="Total datasources"
            value={data?.dataSources?.length}
          />
          <Trend
            label="Total events last 7 days"
            value={data?.rollupMetrics?.eventsLast7days}
            summarizeCount
            trend={{
              startingValue: data?.rollupMetrics?.eventsPreceding7days ?? 0,
              endingValue: data?.rollupMetrics?.eventsLast7days ?? 0,
            }}
          />

          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <Text
                size="2xl"
                bold
                className="text-green-600 dark:text-green-600"
              >
                {data?.dataSources?.filter((q) => q.alerts?.currentlyOpen === 0)
                  .length ?? 0}
              </Text>
              <Text>OK</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text size="2xl" bold className="text-red-600 dark:text-red-600">
                {data?.dataSources?.filter((q) => !!q.alerts?.currentlyOpen)
                  .length ?? 0}
              </Text>
              <Text>Need Attention</Text>
            </div>
          </div>

          <Trend
            label="Total events last 24 hours"
            value={data?.rollupMetrics?.eventsLast24hours}
            summarizeCount
            trend={{
              startingValue: data?.rollupMetrics?.eventsPreceding24hours ?? 0,
              endingValue: data?.rollupMetrics?.eventsLast24hours ?? 0,
            }}
          />
        </div>

        <div className="col-span-1 flex h-full w-full flex-col">
          <Text>Data processed last 7 days</Text>
          <BarChart
            data={barChartData}
            dataKey="value"
            className="mt-auto"
            tooltip={{
              formatter: (value) => `${count(value as number)} events`,
              position: { y: 0 },
            }}
          />
        </div>
      </div>
    </BaseHomepageSummaryCard>
  );
};

export const DataSourceHomepageSummaryCardSkeleton = () => (
  <HomepageSummaryCardSkeleton
    title="Datasources"
    subtitle="Total datasources"
    to="/configure/data-sources"
    showChart
    showStatus
    totalsMetric="events"
  />
);

export const DataSourceHomepageSummaryCardErrorFallback = (
  props: FallbackProps,
) => {
  return (
    <BaseHomepageSummaryCard title="Datasources" to="/configure/data-sources">
      <DefaultErrorBoundaryFallback {...props} />
    </BaseHomepageSummaryCard>
  );
};
